export default {
    title: 'Performance Review',
    prepareAssessment: 'Prepare for the Performance Assessment',
    prepareAssessmentDesc:
        'Add key discussion points as you complete the evaluation to ensure a focused and productive conversation with the employee.',
    evaluationDate: 'Review Date',
    evaluationPeriod: 'Review Period',
    evaluationPeriodInheritedHint: 'Period defined in the feedback cycle. You can adjust it.',
    selectPeriod: 'Select a period',
    customPeriodOption: 'Custom period',
    customPeriodsHeader: 'Custom',
    predefinedPeriodsHeader: 'Predefined periods',
    periodFrom: 'From',
    periodTo: 'To',
    infoBox:
        'Start by conducting a comprehensive competency assessment of {{name}} to accurately gauge performance and eliminate evaluation bias.',
    employeeCard: {
        selected: 'You selected',
    },
    processDemo: {
        infoButton: 'Info',
        eyebrow: 'Amélio process',
        titleBefore: 'A',
        titleHighlight: 'much easier',
        titleAfter: ' cycle',
        stepLabel: 'Step {{index}}',
        step1Title: 'HR creates the cycle',
        connectorGenerates: 'automatically generates',
        simultaneous: 'simultaneously',
        feedback360Title: '360° Feedback',
        feedback360Desc: 'The good reviewers suggested by the recommendation engine.',
        selfEvalTitle: 'Self-Assessment',
        connectorFeeds: 'nourish',
        step3Title: 'The assessment by {{managerLabel}}',
        step3Desc:
            'A look at situations, not an arbitrary judgment.\n\n→ Eliminates bias\n→ Fairness between teams\n→ Reduce the burden on each {{managerLabel}}',
    },
    steps: {
        evaluation: 'Evaluation',
        results: 'Results',
        prepareMeeting: 'Prepare Meeting',
        meet: 'Meet',
        navLabel: 'Assessment steps',
        stepAriaLabel: 'Step {{rank}} of {{total}}: {{label}}',
        progressLabel: 'Assessment progress',
        progressValue: 'Step {{rank}} of {{total}}',
    },
    survey: {
        title: 'Performance Survey',
        skip: 'Skip this question',
        addNotes: 'Add a note',
        notesLabel: 'Note on this question',
        notesPlaceholder: 'Write your notes here...',
        notesDisclaimer: 'Visible to you and your management line, never to the employee.',
        notesSaving: 'Saving...',
        notesSaveError: 'The note could not be saved',
        notesSaved: 'Saved',
        notesUnavailable:
            'Your note could not be loaded. The field stays read-only: writing now could create a second note. Reload the page to try again.',
        notesReadOnly: 'This note was written by someone else: you can read it, not change it.',
        notesDeleteTitle: 'Delete the note?',
        notesDeleteMessage:
            "You emptied the field. This note will be removed from the employee's profile and cannot be recovered.",
        previousQuestion: 'Previous Question',
        nextQuestion: 'Next Question',
        submit: 'Submit Evaluation',
        mandatoryUnanswered:
            'Question {{number}} is mandatory. Answer it before submitting the evaluation.',
        ratingOptions: {
            exceeds: 'Exceeds expectations, Consistently demonstrates mastery',
            meets: 'Meets expectations, Solid performance with minor areas for growth',
            needsImprovement: 'Needs improvement, Making progress but requires follow-up',
            below: 'Below expectations, Significant development needed',
        },
    },
    quickAssessment: {
        title: 'Quick Assessment',
        select: 'Select',
        submit: 'Submit Evaluation',
        companyValuesGroup: 'Company Values',
    },
    editCompetencies: {
        button: 'Edit Competencies',
        title: 'Edit competencies',
        subtitle: 'Select competencies to include in this evaluation',
        searchPlaceholder: 'Search a competency...',
        selectAll: 'Select all',
        deselectAll: 'Deselect all',
        selectedCount: '{{selected}} / {{total}} selected',
        companyValues: 'Assess adherence to company values',
        companyValuesDescription:
            'You can include a company values adherence evaluation in the performance survey.',
        includeCompanyValuesQuestions: 'Include company values questions in the survey',
        empty: 'No competencies found.',
        cancel: 'Cancel',
        save: 'Save ({{count}})',
        noCompetencyHint:
            "Looking for a competency you can't find here? You'll need to add it to the employee's profile first.",
    },
    completion: {
        readyTitle: "You have completed {{name}}'s evaluation.",
        readySubtitle: 'Click to submit.',
        alreadySubmittedTitle: "You have already submitted {{name}}'s evaluation.",
        alreadySubmittedSubtitle:
            'Your answers are saved. Change an answer to re-submit, or view the results.',
        title: 'Well done!',
        subtitle: 'The performance survey has been completed successfully.',
        description:
            'Your answers have been saved. You can now review the results or prepare the meeting.',
        viewResults: 'View Results',
        backToAssessment: 'Back to assessments',
    },
    insights: {
        title: 'Insights',
        aiGenerated: 'AI Generated',
        copySummary: 'Copy summary',
        copied: 'Copied',
        showMore: 'Show more',
        showLess: 'Show less',
        generate: 'Generate insights',
        generating: 'Generating…',
        generationStartedTitle: 'Generating insights',
        generationStartedMessage:
            "We're preparing your insights, they'll appear here shortly. Please wait.",
        generationFailed: "Couldn't generate this insight. Please try again.",
        noResponses:
            'No responses to analyze yet. Insights become available as soon as answers come in.',
        belowConfidentialityThreshold:
            'Not enough responses to generate insights yet. More responses are needed to protect confidentiality.',
        serviceUnavailable:
            "Insights couldn't be generated right now because the AI service did not respond. Please try again in a few minutes.",
        unusableOutput:
            "Insights couldn't be generated from this evaluation. Please try again, and let us know if the problem continues.",
        unexpectedError:
            "Insights couldn't be generated because something went wrong. Please try again, and contact support@amelio.co if the problem continues.",
        retry: 'Retry',
        periodRequired: 'Set the reference period first to generate insights.',
        empty: 'No insights yet. Generate them to see context from engagement, 360 and self-assessment.',
        engagementSection: 'Insights from engagement',
        peer360Section: 'Insights from 360 review',
        selfSection: 'Insights from self-assessment',
        noData: 'No relevant data for this question.',
    },
    meetingTopics: {
        title: 'Prepare meeting topics with {{name}}',
        description:
            'Add key themes you would like to bring up during the meeting. These can be linked to insights from the performance assessment.',
        addPoint: 'Add a meeting point',
        dateAdded: 'Date added',
        detailsAdded: 'Details added',
        noTopics: 'No topics added for this evaluation.',
        modalEditTitle: 'Edit Meeting Point',
        modalSubjectLabel: 'Discussion topic',
        modalNotesLabel: 'Notes',
        modalNotesPlaceholder: 'Write the notes for this meeting topic…',
        saveChanges: 'Save Changes',
        deleteTitle: 'Delete this meeting topic?',
        deleteMessage:
            '“{{subject}}” will be removed from the meeting preparation and cannot be recovered.',
        deleteMessageWithNotes:
            '“{{subject}}” and its notes will be removed from the meeting preparation and cannot be recovered.',
    },
    employeeObjectives: {
        title: '{{name}}’s objectives',
        periodSummary: 'Reference period: {{period}}',
        customPeriod: '{{from}} to {{to}}',
        periodMissing: 'Reference period to be set',
        periodRequired: 'Set the reference period first to see {{name}}’s objectives in it.',
        loadError: 'The objectives could not be loaded.',
    },
    resultsPlaceholder:
        'The results of the evaluation will appear here once the assessment is complete.',
    prepareMeeting: {
        title: 'Prepare your meeting\nwith {{name}}',
        desc: 'Let Amélio AI generate your meeting agenda based on the assessment and insights gathered.',
        button: 'Prepare meeting',
    },
    readOnly: {
        message:
            'This assessment is completed and read-only. Historical records cannot be modified.',
    },
    customPeriodInvalid: 'The end date must be on or after the start date.',
};
